import { Contact, GuestParticipant } from '../types';

/**
 * Pure Logik der Teilnehmerauswahl einer Begehung (Suche, Umschalten, Gaeste).
 *
 * Liegt bewusst neben der Komponente und nicht in ihr: das Projekt hat kein
 * jsdom, eine .tsx ist damit nicht testbar. ParticipantSheet.tsx bleibt eine
 * duenne Huelle ueber diesen Funktionen — vgl. services/listKeyboardNav.ts.
 */

export interface ParticipantRow {
    kind: 'contact' | 'guest';
    id: string;
    name: string;
    company: string;
    role: string;
    selected: boolean;
}

/** Trim + Mehrfach-Leerzeichen zusammenziehen. Aus "  Max   Muster " wird "Max Muster". */
export const normalizeName = (raw: string): string => (raw || '').replace(/\s+/g, ' ').trim();

/**
 * Zwei Eintraege sind dieselbe Person, wenn die Namen gleich sind und die
 * Firmen sich nicht widersprechen. Eine fehlende Firma widerspricht nie — sonst
 * haengt es an der Eingabereihenfolge, ob "Müller" und "Müller / Fa. A"
 * zusammenfallen.
 */
export const isSamePerson = (
    a: { name: string; company?: string },
    b: { name: string; company?: string },
): boolean => {
    if (normalizeName(a.name).toLowerCase() !== normalizeName(b.name).toLowerCase()) return false;
    const ca = normalizeName(a.company || '').toLowerCase();
    const cb = normalizeName(b.company || '').toLowerCase();
    if (!ca || !cb) return true;
    return ca === cb;
};

/**
 * Kontakte -> Zeilen der Auswahlliste. Die Reihenfolge des Verteilers bleibt
 * erhalten (sortOrder steuert spaeter das PDF), es wird NICHT nach Auswahl
 * umsortiert: eine Liste, die unter dem Finger springt, ist auf dem Handy
 * genau die Falle, in der man den Falschen antippt.
 */
export const toContactRows = (contacts: Contact[]): ParticipantRow[] =>
    (contacts || []).map(c => ({
        kind: 'contact' as const,
        id: c.id,
        name: c.name || '',
        company: c.company || '',
        role: c.role || '',
        selected: c.present === true,
    }));

export const toGuestRows = (guests: GuestParticipant[]): ParticipantRow[] =>
    (guests || []).map(g => ({
        kind: 'guest' as const,
        id: g.id,
        name: g.name || '',
        company: g.company || '',
        role: g.role || '',
        selected: true,
    }));

/** Suche ueber Name, Firma und Rolle. Leere Suche liefert alles. */
export const filterRows = (rows: ParticipantRow[], query: string): ParticipantRow[] => {
    const q = normalizeName(query).toLowerCase();
    if (!q) return rows;
    const terms = q.split(' ');
    return rows.filter(r => {
        const haystack = `${r.name} ${r.company} ${r.role}`.toLowerCase();
        return terms.every(t => haystack.includes(t));
    });
};

/** Ein Kontakt an/aus. */
export const toggleContactPresence = (contacts: Contact[], id: string): Contact[] =>
    (contacts || []).map(c => (c.id === id ? { ...c, present: !c.present } : c));

/** Massenaktion, wirkt nur auf die uebergebenen Ids (also auf das GEFILTERTE Ergebnis). */
export const setPresenceForIds = (contacts: Contact[], ids: string[], present: boolean): Contact[] => {
    const set = new Set(ids);
    return (contacts || []).map(c => (set.has(c.id) ? { ...c, present } : c));
};

/**
 * Alle Anwesenheiten zuruecksetzen. Wird beim Anlegen des Folgeberichts
 * gebraucht: die Teilnehmer der letzten Begehung sind keine Aussage ueber die
 * naechste, und ein uebersehener Haken behauptet im Protokoll eine Anwesenheit,
 * die es nie gab.
 */
export const clearPresence = (contacts: Contact[]): Contact[] =>
    (contacts || []).map(c => (c.present ? { ...c, present: false } : c));

export interface GuestDraft {
    name: string;
    company?: string;
    role?: string;
}

/**
 * Bewusst EIN flaches Interface statt einer diskriminierten Union: das Projekt
 * kompiliert ohne `strict`, und ohne `strictNullChecks` verengt TypeScript ein
 * `if (!res.ok)` nicht auf den Fehlerzweig — die Aufrufer kaemen an `message`
 * und `reason` gar nicht heran. Im Fehlerfall traegt `guests` die unveraenderte
 * Liste, ein blindes Uebernehmen des Rueckgabewerts verliert also nichts.
 */
export interface AddGuestResult {
    ok: boolean;
    guests: GuestParticipant[];
    added?: GuestParticipant;
    reason?: 'empty' | 'duplicate-guest' | 'duplicate-contact';
    message?: string;
    existingId?: string;
}

/**
 * Gast anlegen. Scheitert bei leerem Namen und bei Dubletten — ein zweiter
 * "Müller" derselben Firma in derselben Liste ist im Protokoll nicht
 * unterscheidbar. Deckt sich der Gast mit einem Projektbeteiligten, wird
 * dessen Id zurueckgegeben, damit die Oberflaeche stattdessen dessen Haken
 * setzen kann.
 */
export const addGuest = (
    guests: GuestParticipant[],
    contacts: Contact[],
    draft: GuestDraft,
    newId: string,
): AddGuestResult => {
    const unchanged = guests || [];
    const name = normalizeName(draft.name);
    if (!name) return { ok: false, guests: unchanged, reason: 'empty', message: 'Bitte einen Namen eingeben.' };

    const company = normalizeName(draft.company || '');
    const role = normalizeName(draft.role || '');
    const candidate = { name, company };

    const existingGuest = unchanged.find(g => isSamePerson(g, candidate));
    if (existingGuest) {
        return { ok: false, guests: unchanged, reason: 'duplicate-guest', message: `${name} steht bereits als Gast in der Liste.`, existingId: existingGuest.id };
    }

    const existingContact = (contacts || []).find(c => isSamePerson(c, candidate));
    if (existingContact) {
        return {
            ok: false,
            guests: unchanged,
            reason: 'duplicate-contact',
            message: `${name} ist bereits Projektbeteiligter und wurde als Teilnehmer markiert.`,
            existingId: existingContact.id,
        };
    }

    const added: GuestParticipant = { id: newId, name, ...(company ? { company } : {}), ...(role ? { role } : {}) };
    return { ok: true, guests: [...unchanged, added], added };
};

export const removeGuest = (guests: GuestParticipant[], id: string): GuestParticipant[] =>
    (guests || []).filter(g => g.id !== id);

/**
 * Mehrere Gaeste entfernen. Braucht die Massenaktion "Keine": ein Gast ist
 * immer anwesend, also muss er verschwinden, wenn niemand mehr anwesend sein
 * soll — sonst zeigt der Zaehler danach Teilnehmer an, obwohl der Knopf das
 * Gegenteil versprochen hat.
 */
export const removeGuests = (guests: GuestParticipant[], ids: string[]): GuestParticipant[] => {
    const set = new Set(ids);
    return (guests || []).filter(g => !set.has(g.id));
};

/** Zaehler fuer Kopfzeile und Knopfbeschriftung. */
export const countSelected = (contacts: Contact[], guests: GuestParticipant[]): number =>
    (contacts || []).filter(c => c.present).length + (guests || []).length;
